// https://www.figma.com/design/fA3TW1qmAOaU4ll7ihIDVj/Kudakwashe_FF?node-id=0-1&node-type=canvas&t=rFiZhL28uqsjAMRO-0
import '@fontsource/urbanist/300.css'; // Light
import '@fontsource/urbanist/400.css'; // Regular
import '@fontsource/urbanist/500.css'; // Medium
import '@fontsource/urbanist/700.css'; // Bold
import '~/styles/index.css';

import { Helmet } from 'react-helmet-async';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Grid } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import ReactGA from 'react-ga4';
import posthog from 'posthog-js';
import { useEffect } from 'react';

import { config, CONFIG } from '~/config';

import { useAppSelector } from '~/modules/hooks';

import Footer from '~/components/Footer';
import Header from '~/components/Header';
import PrivateRoute from '~/components/PrivateRoute';
import PublicRoute from '~/components/PublicRoute';
import FlightBooking from '~/routes/FlightBooking';
import FlightHomepage from '~/routes/FlightHomepage';
import LoginPage from '~/routes/Login';
import NotFound from '~/routes/NotFound';

import { selectUser } from '~/selectors';
import TripengerTheme from '~/styles/theme';

import ForgotPasswordPage from './routes/ForgotPassword';
import RegisterPage from './routes/Register';
import FlightReservationPage from './routes/FlightReservation';
import FlightPayment from './routes/FlightPayment';
import ItineraryBuy from './routes/ItineraryBuy';
import ItineraryHome from './routes/ItineraryHome';
import ItineraryWizard from './routes/ItineraryWizard';
import ChangePasswordPage from './routes/ChangePassword';
import ManageTripsPage from './routes/ManageTrips';
import ManageTripPage from './routes/ManageTrip';
import UserProfile from './routes/UserProfile';
import ProtectedRoute from './containers/ProtectedRoute';
import TopBarAlert from './components/TopBarAlert';
import GDPRConsentModal, { hasAcceptedTrackingConsent } from './components/GDPRConsentModal';

let isGoogleAnalyticsInitialized = false;
let isPostHogInitialized = false;

const initializeGoogleAnalytics = () => {
  if (!config.app.googleAnalyticsId || isGoogleAnalyticsInitialized || !hasAcceptedTrackingConsent()) {
    return false;
  }

  ReactGA.initialize([
    { trackingId: config.app.googleAnalyticsId },
    { trackingId: config.app.googleAdsId },
  ]);
  isGoogleAnalyticsInitialized = true;
  return true;
};

const initializePostHog = () => {
  if (
    !config.app.posthogProjectApiKey
    || !config.app.posthogApiHost
    || isPostHogInitialized
    || !hasAcceptedTrackingConsent()
  ) {
    return false;
  }

  posthog.init(config.app.posthogProjectApiKey, {
    api_host: config.app.posthogApiHost,
    defaults: config.app.posthogDefaults,
    person_profiles: config.app.posthogPersonProfiles,
    autocapture: true,
    capture_pageview: false,
    persistence: 'localStorage+cookie',
    loaded: (posthogInstance) => {
      posthogInstance.opt_in_capturing();
    },
  });

  isPostHogInitialized = true;
  return true;
};

const captureAnalyticsPageView = (page: string) => {
  if (isGoogleAnalyticsInitialized) {
    ReactGA.send({ hitType: 'pageview', page });
  }

  if (isPostHogInitialized) {
    posthog.capture('$pageview', {
      $current_url: window.location.href,
      path: page,
    });
  }
};

function Root() {
  const user = useAppSelector(selectUser);
  const { isAuthenticated } = user;
  const location = useLocation();

  useEffect(() => {
    initializeGoogleAnalytics();
    initializePostHog();

    const handleConsentChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ choice?: string }>;

      if (customEvent.detail?.choice === 'accept') {
        initializeGoogleAnalytics();
        initializePostHog();
        captureAnalyticsPageView(window.location.pathname + window.location.search);
      }
    };

    window.addEventListener('tripenger:gdpr-consent-change', handleConsentChange);

    return () => {
      window.removeEventListener('tripenger:gdpr-consent-change', handleConsentChange);
    };
  }, []);

  useEffect(() => {
    initializeGoogleAnalytics();
    initializePostHog();

    captureAnalyticsPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const lastPathKey = 'tpwl-last-pathname';
    const reloadKey = 'tpwl-homepage-reloaded';
    const previousPath = window.sessionStorage.getItem(lastPathKey);

    if (location.pathname !== '/') {
      window.sessionStorage.removeItem(reloadKey);
    } else if (previousPath && previousPath !== '/' && !window.sessionStorage.getItem(reloadKey)) {
      window.sessionStorage.setItem(reloadKey, '1');
      window.location.reload();
      return;
    }

    window.sessionStorage.setItem(lastPathKey, location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname, location.search]);
  // Component used to force a full-page reload when navigating to /info/*
  function InfoReload() {
    useEffect(() => {
      // If we're running locally (no HAProxy/nginx in front), reload will just
      // re-mount the SPA and re-trigger this route, causing an infinite loop.
      // In that case, redirect to the external help site instead so developers
      // see the real content.
      const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (isLocal) {
        const helpBase = 'https://www.tripenger.com'.replace(/\/$/, '');
        const rest = window.location.pathname.replace(/^\/info/, '') || '/';
        const target = `${helpBase}${rest}${window.location.search}`;
        if (window.location.href !== target) window.location.replace(target);
        return;
      }

      // In non-local environments, perform a full reload so the origin server
      // (HAProxy) can handle the /info request.
      window.location.reload();
    }, []);
    return null;
  }

  return (
    <ThemeProvider theme={TripengerTheme}>
      <Helmet
        defaultTitle={`${CONFIG.app.description}`}
        defer={false}
        encodeSpecialCharacters
        htmlAttributes={{ lang: 'pt-br' }}
        titleAttributes={{ itemprop: 'name', lang: 'pt-br' }}
        titleTemplate={`%s`}
      />
      <GDPRConsentModal />
      {(CONFIG.env.featureFlag.onlyRegisteredUsersCanAccess && !isAuthenticated) && <TopBarAlert />}
      <Grid container direction="column" sx={{ minHeight: '90vh' }}>
        <Grid item>
          <Header isAuthenticated={isAuthenticated} />
        </Grid>
        <Grid className="main-content" item>
          <Routes>
            <Route
              element={
                <PublicRoute isAuthenticated={isAuthenticated}>
                  <LoginPage />
                </PublicRoute>
              }
              path="/login"
            />
            <Route
              element={
                <PublicRoute isAuthenticated={isAuthenticated}>
                  <ForgotPasswordPage />
                </PublicRoute>
              }
              path="/forgot-password"
            />
            <Route
              element={
                <PublicRoute isAuthenticated={isAuthenticated}>
                  <ChangePasswordPage />
                </PublicRoute>
              }
              path="/user/reset"
            />
            <Route element={<ProtectedRoute><FlightHomepage /></ProtectedRoute>} path="/" />
            <Route element={<ProtectedRoute><FlightHomepage /></ProtectedRoute>} path="/cheap-flights/:originIata/:destinationIata" />
            <Route element={<ProtectedRoute><ItineraryBuy /></ProtectedRoute>} path="/itinerary/:packageId/buy" />
            <Route element={<ProtectedRoute><ItineraryWizard /></ProtectedRoute>} path="/itinerary/:orderId/:paymentResult" />
            <Route element={<ProtectedRoute><ItineraryWizard /></ProtectedRoute>} path="/itinerary/:orderId" />
            <Route element={<ProtectedRoute><ItineraryHome /></ProtectedRoute>} path="/itinerary" />
            <Route element={<Navigate replace to="/" />} path="/flights" />
            <Route element={<ProtectedRoute><UserProfile /></ProtectedRoute>} path="/my-account" />
            <Route element={<ProtectedRoute><ManageTripsPage /></ProtectedRoute>} path="/manage" />
            <Route element={<ProtectedRoute><ManageTripPage /></ProtectedRoute>} path="/manage/:id" />
            <Route element={<ProtectedRoute><FlightBooking /></ProtectedRoute>} path="/flights/pre-booking/:id" />
            <Route element={<ProtectedRoute><FlightPayment /></ProtectedRoute>} path="/flights/payment/:id" />
            <Route element={<ProtectedRoute><FlightReservationPage /></ProtectedRoute>} path="/flights/post-booking/:id" />
            <Route
              element={
                <PublicRoute isAuthenticated={isAuthenticated}>
                  <RegisterPage />
                </PublicRoute>
              }
              path="/register"
            />
            {isAuthenticated && (
              <Route
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <></>
                  </PrivateRoute>
                }
                path="/authpathonly"
              />
            )}
            <Route element={<InfoReload />} path="/info/*" />
            <Route element={<NotFound />} path="*" />
          </Routes>
        </Grid>
        <Grid item>
          <Footer />
        </Grid>
      </Grid>
    </ThemeProvider>
  );
}

export default Root;
